import { getCustomisationValue, updateCustomisationValue } from "../EditablePreview";
import F2uReward from "./f2u/F2uReward";
import GenericProgressBar from "./generic-progress-bar/GenericProgressBar";
import PositionableProgressBar from "./generic-progress-bar/PositionableProgressBar";
import LivePoll from "./live-poll/LivePoll";
import FallingObjects from "./object-zapper/FallingObjects";
import WaveHands from "./wave/wave-hands/WaveHands";
import WaveProgressBar from "./wave/wave-progress-bar/WaveProgressBar";
import WaveWin from "./wave/wave-win/WaveWin";

export const CUSTOM_COMPONENTS_LIST = {
    // Live poll is a top level renderer, the key here matches the id of the feature in the customisation structure
    livePoll: {
        render: (props) => <LivePoll {...props} />,
        convertStoredValueToVisualisedValue: (field, value, path, node) => {
            console.log("convertStoredValueToVisualisedValue", field, value, path, node);
            if (path.endsWith('panelWidth')) {
                // Live Poll panel width is a special case, it is a stored as a percentage where 100% is 720 pixels wide,
                // we need to convert this to a pixel value for the visualised node
                const panelWidth = (720 * (value / 100)) + 60;
                console.log("Converted panelWidth to pixels:", panelWidth);
                return panelWidth;
            }
            if (path.endsWith('hashtagAreaWidth')) {
                console.log("Converted hashtagAreaWidth to pixels:", value + 50);
                return value + 50;
            }
            if (path.endsWith('imageWidth')) {
                const panelWidth = getCustomisationValue(node.customisationVars, 'hud.polls.shapeSettings.panelWidth');
                const borderWidth = getCustomisationValue(node.customisationVars, 'hud.polls.shapeSettings.borderThickness');
                const imageWidthRatio = value / 100;
                const imageWidth = Math.round((panelWidth - borderWidth * 2) * imageWidthRatio);
                console.log("Converted imageWidth to pixels:", imageWidth);
                return imageWidth;
            }
            return value;
        },
        convertVisualisedValueToStoredValue: (field, value, path, node) => {
            console.log("convertVisualisedValueToStoredValue", field, value, path, node);
            if (path.endsWith('panelWidth')) {
                // We need to convert the pixel value back to a percentage for storage
                const panelWidth = (((value - 60) / 720) * 100);
                console.log("Converted panelWidth to percentage:", panelWidth);
                return panelWidth;
            }
            if (path.endsWith('hashtagAreaWidth')) {
                console.log("Converted hashtagAreaWidth:", value - 50);
                let newVal = value - 50;
                if (newVal < 0) {
                    newVal = 0; // Ensure it doesn't go negative
                }
                return newVal;
            }
            if (path.endsWith('imageWidth')) {
                const panelWidth = getCustomisationValue(node.customisationVars, 'hud.polls.shapeSettings.panelWidth');
                const borderWidth = getCustomisationValue(node.customisationVars, 'hud.polls.shapeSettings.borderThickness');
                const innerWidth = panelWidth - (borderWidth * 2);
                if (innerWidth <= 0) {
                    return 2; // Avoid division by zero
                }
                let imageWidthPerc = (value / innerWidth) * 100;
                if (imageWidthPerc < 2) {
                    imageWidthPerc = 2; // Ensure it doesn't go negative
                }
                if (imageWidthPerc > 100) {
                    imageWidthPerc = 100; // Cap at 100%
                }
                console.log("Converted imageWidth to percentage:", imageWidthPerc);
                return imageWidthPerc;
            }
            return value;
        },
    },
    // Wave Progress Bar is not a top level renderer, it has to be set on a group node to specify how it is rendered
    // Multiple custom renderers can be used for the same feature in this way
    waveProgressBar: {
        render: (props) => <WaveProgressBar {...props} />,
        convertStoredValueToVisualisedValue: (field, value, path, node) => {
            console.log("convertStoredValueToVisualisedValue", field, value, path, node);
            if (path.endsWith('shape.length')) {
                // Check our orientation, we might need to swap width and height
                const positionSetting = getCustomisationValue(node.customisationVars, 'progressBar.shapeAndPosition.position.position');
                const dir = (positionSetting === 'left' || positionSetting === 'right') ? 'vertical' : 'horizontal';

                // Wave Progress Bar length is stored as a percentage, convert it to pixels
                // For vertical bars, we spit out the thickness value instead
                const length = dir === 'horizontal' ? Math.round(1920 * (value / 100)) : getCustomisationValue(node.customisationVars, 'progressBar.shapeAndPosition.shape.thickness');
                console.log("Converted shape.length to pixels:", length);
                return length;
            } else 
            if (path.endsWith('shape.thickness')) {
                // Check our orientation, we might need to swap width and height
                const positionSetting = getCustomisationValue(node.customisationVars, 'progressBar.shapeAndPosition.position.position');
                const dir = (positionSetting === 'left' || positionSetting === 'right') ? 'vertical' : 'horizontal';

                const thickness = dir === 'vertical' ? Math.round(1080 * (getCustomisationValue(node.customisationVars, 'progressBar.shapeAndPosition.shape.length') / 100)) : value;
                console.log("Converted shape.thickness to pixels:", thickness);
                return thickness;
            }
            return value;
        },
        convertVisualisedValueToStoredValue: (field, value, path, node) => {
            console.log("convertVisualisedValueToStoredValue", field, value, path, node);
            if (path.endsWith('shape.length')) {
                // Check our orientation, we might need to swap width and height
                const positionSetting = getCustomisationValue(node.customisationVars, 'progressBar.shapeAndPosition.position.position');
                const dir = (positionSetting === 'left' || positionSetting === 'right') ? 'vertical' : 'horizontal';

                // Convert the pixel value back to a percentage for storage
                if (dir === 'horizontal') {
                    const length = ((value / 1920) * 100);
                    console.log("Converted shape.length to percentage:", length);
                    return length;
                } else
                if (dir === 'vertical') {
                    // We have actually been passed in the thickness value, which needs storing in 'progressBar.shapeAndPosition.shape.thickness'
                    updateCustomisationValue(node.customisationVars, 'progressBar.shapeAndPosition.shape.thickness', value);
                    // Return the correct value for this path, which is just pulled out of our data because the next thing the calling function will do is store it.
                    const realLengthValue = getCustomisationValue(node.customisationVars, 'progressBar.shapeAndPosition.shape.length');
                    return realLengthValue;
                }
            } else
            if (path.endsWith('shape.thickness')) {
                // Check our orientation, we might need to swap width and height
                const positionSetting = getCustomisationValue(node.customisationVars, 'progressBar.shapeAndPosition.position.position');
                const dir = (positionSetting === 'left' || positionSetting === 'right') ? 'vertical' : 'horizontal';

                // Convert the pixel value back to a percentage for storage
                if (dir === 'horizontal') {
                    // For horizontal bars we have received a thickness value that just needs returning as-is
                    return value;
                } else
                if (dir === 'vertical') {
                    // For vertical bars we have actually been passed a length value that needs processing and storing in 'progressBar.shapeAndPosition.shape.length'
                    const length = ((value / 1080) * 100);
                    console.log("Converted shape.length to percentage:", length);
                    updateCustomisationValue(node.customisationVars, 'progressBar.shapeAndPosition.shape.length', length);
                    // We need to retrieve the actual thickness value and return that as-is because the next thing the calling function will do is store it.
                    const realThicknessValue = getCustomisationValue(node.customisationVars, 'progressBar.shapeAndPosition.shape.thickness');
                    return realThicknessValue;
                }
            }

            return value;
        },
    },
    waveHands: {
        render: (props) => <WaveHands {...props} />,
        convertStoredValueToVisualisedValue: (field, value, path, node) => {
            console.log("convertStoredValueToVisualisedValue", field, value, path, node);
            /* if (path.endsWith('shape.length')) {
                // Wave Hands length is stored as a percentage, convert it to pixels
                const length = Math.round(1920 * (value / 100));
                console.log("Converted shape.length to pixels:", length);
                return length;
            } */
            return value;
        },
        convertVisualisedValueToStoredValue: (field, value, path, node) => {
            console.log("convertVisualisedValueToStoredValue", field, value, path, node);
            /* if (path.endsWith('shapeAndPosition.shape.length')) {
                // Convert the pixel value back to a percentage for storage
                const length = ((value / 1920) * 100);
                console.log("Converted shape.length to percentage:", length);
                return length;
            } */
            return value;
        },
    },
    waveWin: {
        render: (props) => <WaveWin {...props} />,
    },
    f2uReward: {
        render: (props) => {
            console.log("Render F2uReward props:", props);
            return <F2uReward {...props} />;
        }
    },
    positionableProgressBar: {
        render: (props) => <PositionableProgressBar {...props} />,
    },
    // Generic Progress Bar is not a top level renderer, it has to be set on a group node to specify how it is rendered
    // Multiple custom renderers can be used for the same feature in this way
    genericProgressBar: {
        render: (props) => <GenericProgressBar {...props} />,
        /* convertStoredValueToVisualisedValue: (field, value, path, node) => {
            console.log("convertStoredValueToVisualisedValue", field, value, path, node);
            if (path.endsWith('shape.length')) {
                // Generic Progress Bar length is stored as a percentage, convert it to pixels
                const length = Math.round(1920 * (value / 100));
                console.log("Converted shape.length to pixels:", length);
                return length;
            }
            return value;
        },
        convertVisualisedValueToStoredValue: (field, value, path, node) => {
            console.log("convertVisualisedValueToStoredValue", field, value, path, node);
            if (path.endsWith('shapeAndPosition.shape.length')) {
                // Convert the pixel value back to a percentage for storage
                const length = ((value / 1920) * 100);
                console.log("Converted shape.length to percentage:", length);
                return length;
            }
            return value;
        }, */
        convertStoredValueToVisualisedValue: (field, value, path, node) => {
            console.log("convertStoredValueToVisualisedValue", field, value, path, node);
            if (path.endsWith('shape.length')) {
                // Check our orientation, we might need to swap width and height
                const positionSetting = getCustomisationValue(node.customisationVars, 'progressBar.shapeAndPosition.position.position');
                const dir = (positionSetting === 'left' || positionSetting === 'right') ? 'vertical' : 'horizontal';

                // Wave Progress Bar length is stored as a percentage, convert it to pixels
                // For vertical bars, we spit out the thickness value instead
                const length = dir === 'horizontal' ? Math.round(1920 * (value / 100)) : getCustomisationValue(node.customisationVars, 'progressBar.shapeAndPosition.shape.thickness');
                console.log("Converted shape.length to pixels:", length);
                return length;
            } else 
            if (path.endsWith('shape.thickness')) {
                // Check our orientation, we might need to swap width and height
                const positionSetting = getCustomisationValue(node.customisationVars, 'progressBar.shapeAndPosition.position.position');
                const dir = (positionSetting === 'left' || positionSetting === 'right') ? 'vertical' : 'horizontal';

                const thickness = dir === 'vertical' ? Math.round(1080 * (getCustomisationValue(node.customisationVars, 'progressBar.shapeAndPosition.shape.length') / 100)) : value;
                console.log("Converted shape.thickness to pixels:", thickness);
                return thickness;
            }
            return value;
        },
        convertVisualisedValueToStoredValue: (field, value, path, node) => {
            console.log("convertVisualisedValueToStoredValue", field, value, path, node);
            if (path.endsWith('shape.length')) {
                // Check our orientation, we might need to swap width and height
                const positionSetting = getCustomisationValue(node.customisationVars, 'progressBar.shapeAndPosition.position.position');
                const dir = (positionSetting === 'left' || positionSetting === 'right') ? 'vertical' : 'horizontal';

                // Convert the pixel value back to a percentage for storage
                if (dir === 'horizontal') {
                    const length = ((value / 1920) * 100);
                    console.log("Converted shape.length to percentage:", length);
                    return length;
                } else
                if (dir === 'vertical') {
                    // We have actually been passed in the thickness value, which needs storing in 'progressBar.shapeAndPosition.shape.thickness'
                    updateCustomisationValue(node.customisationVars, 'progressBar.shapeAndPosition.shape.thickness', value);
                    // Return the correct value for this path, which is just pulled out of our data because the next thing the calling function will do is store it.
                    const realLengthValue = getCustomisationValue(node.customisationVars, 'progressBar.shapeAndPosition.shape.length');
                    return realLengthValue;
                }
            } else
            if (path.endsWith('shape.thickness')) {
                // Check our orientation, we might need to swap width and height
                const positionSetting = getCustomisationValue(node.customisationVars, 'progressBar.shapeAndPosition.position.position');
                const dir = (positionSetting === 'left' || positionSetting === 'right') ? 'vertical' : 'horizontal';

                // Convert the pixel value back to a percentage for storage
                if (dir === 'horizontal') {
                    // For horizontal bars we have received a thickness value that just needs returning as-is
                    return value;
                } else
                if (dir === 'vertical') {
                    // For vertical bars we have actually been passed a length value that needs processing and storing in 'progressBar.shapeAndPosition.shape.length'
                    const length = ((value / 1080) * 100);
                    console.log("Converted shape.length to percentage:", length);
                    updateCustomisationValue(node.customisationVars, 'progressBar.shapeAndPosition.shape.length', length);
                    // We need to retrieve the actual thickness value and return that as-is because the next thing the calling function will do is store it.
                    const realThicknessValue = getCustomisationValue(node.customisationVars, 'progressBar.shapeAndPosition.shape.thickness');
                    return realThicknessValue;
                }
            }

            return value;
        },
    },
    fallingObjects: {
        render: (props) => {
            console.log("Render FallingObjects props:", props);
            return <FallingObjects {...props} />;
        }
    }
}
